import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { behaviorStatsAPI } from '../../services/api';

const sectionSx = {
  mb: 2.5,
  px: 1.75,
  py: 1.25,
  borderRadius: 1.5,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
};

const SimilarDaysVisualization = ({ referenceDate, t }) => {
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [stats, setStats] = React.useState(null);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await behaviorStatsAPI.getSimilarDays(referenceDate, 10);

        if (!response || response.length === 0) {
          setData([]);
          setStats(null);
          return;
        }

        const chartData = response
          .filter((day) => day.similar_date !== referenceDate)
          .slice(0, 8)
          .map((day, idx) => ({
            id: idx,
            date: day.similar_date,
            dateShort: new Date(day.similar_date).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
            }),
            distance: parseFloat((day.distance || 0).toFixed(2)),
            panic: Math.round((day.panic_score || 0) * 100),
            fomo: Math.round((day.fomo_score || 0) * 100),
            fwd_20d: day.fwd_return_20d != null
              ? parseFloat((day.fwd_return_20d * 100).toFixed(2))
              : null,
            fwd_20d_raw: day.fwd_return_20d,
          }));

        setData(chartData);

        const validReturns = chartData.filter((d) => d.fwd_20d_raw != null);
        const returns = validReturns.map((d) => d.fwd_20d_raw);
        const avgReturn = returns.length > 0
          ? (returns.reduce((sum, value) => sum + value, 0) / returns.length * 100)
          : 0;
        const winRate = returns.length > 0
          ? (returns.filter((value) => value > 0).length / returns.length * 100)
          : 0;
        const sortedReturns = [...returns].sort((a, b) => a - b);
        const mid = Math.floor(sortedReturns.length / 2);
        const medianReturn = sortedReturns.length > 0
          ? (sortedReturns.length % 2 === 0
            ? (sortedReturns[mid - 1] + sortedReturns[mid]) / 2
            : sortedReturns[mid]) * 100
          : 0;

        setStats({
          count: validReturns.length,
          avgReturn: parseFloat(avgReturn.toFixed(2)),
          medianReturn: parseFloat(medianReturn.toFixed(2)),
          winRate: parseFloat(winRate.toFixed(1)),
          maxReturn: returns.length > 0 ? Math.max(...returns) * 100 : 0,
          minReturn: returns.length > 0 ? Math.min(...returns) * 100 : 0,
        });
      } catch (err) {
        console.error('Error fetching similar days:', err);
        setError(err.message || 'Failed to load similar days');
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    if (referenceDate) {
      fetchData();
    }
  }, [referenceDate]);

  const formatSignedPercent = (value) => `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;

  const summaryItems = stats ? [
    {
      label: t('sentiment.behavior.similar_days.count', 'Instances'),
      value: stats.count,
      color: '#0f172a',
    },
    {
      label: t('sentiment.behavior.events.typical_return', 'Typical return'),
      value: `${stats.medianReturn >= 0 ? '+' : ''}${stats.medianReturn.toFixed(2)}%`,
      color: stats.medianReturn >= 0 ? '#2e7d32' : '#d32f2f',
    },
    {
      label: t('sentiment.behavior.similar_days.win_rate', 'Win Rate'),
      value: `${stats.winRate.toFixed(0)}%`,
      color: '#0f766e',
    },
    {
      label: t('sentiment.behavior.similar_days.volatility', 'Range'),
      value: t('sentiment.behavior.similar_days.range_value', '{{min}} to {{max}}', {
        min: formatSignedPercent(stats.minReturn),
        max: formatSignedPercent(stats.maxReturn),
      }),
      color: '#2563eb',
    },
  ] : [];

  const renderHeader = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'space-between',
        gap: 1.5,
        flexWrap: 'wrap',
        mb: 1,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary', lineHeight: 1.3 }}>
          {t('sentiment.behavior.similar_days.title', 'Historical Similar Days')}
        </Typography>
      </Box>

      {stats && summaryItems.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: { xs: 2, sm: 2.5 },
            flexWrap: 'wrap',
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
          }}
        >
          {summaryItems.map((item) => (
            <Box
              key={item.label}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 0.15,
                minWidth: 0,
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                {item.label}
              </Typography>
              <Typography sx={{ color: item.color, fontWeight: 800, fontSize: '0.9rem', lineHeight: 1.1 }}>
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={sectionSx}>
        {renderHeader()}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
          <CircularProgress size={32} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={sectionSx}>
        {renderHeader()}
        <Alert severity="error" sx={{ py: 0.5 }}>
          {t('sentiment.behavior.similar_days.error', 'Error loading similar days: ')} {error}
        </Alert>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={sectionSx}>
        {renderHeader()}
        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
          {t('sentiment.behavior.similar_days.empty', 'No similar historical days found')}
        </Typography>
      </Box>
    );
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '8px 12px',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {dataPoint.date}
          </Typography>
          <Typography variant="caption" display="block">
            {t('sentiment.behavior.similar_days.distance', 'Distance')}: {dataPoint.distance.toFixed(3)}
          </Typography>
          <Typography variant="caption" display="block" sx={{ color: '#ff7300' }}>
            {t('sentiment.behavior.similar_days.panic', 'Panic')}: {dataPoint.panic}%
          </Typography>
          <Typography variant="caption" display="block" sx={{ color: '#00c9ff' }}>
            {t('sentiment.behavior.similar_days.fomo', 'FOMO')}: {dataPoint.fomo}%
          </Typography>
          <Typography
            variant="caption"
            display="block"
            sx={{
              fontWeight: 700,
              color: dataPoint.fwd_20d == null ? 'text.secondary' : (dataPoint.fwd_20d >= 0 ? '#4caf50' : '#d32f2f'),
            }}
          >
            {t('sentiment.behavior.similar_days.fwd_20d', 'Fwd 20d')}:{' '}
            {dataPoint.fwd_20d == null
              ? '—'
              : `${dataPoint.fwd_20d >= 0 ? '+' : ''}${dataPoint.fwd_20d.toFixed(2)}%`}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box sx={sectionSx}>
      {renderHeader()}

      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart
          data={data}
          margin={{ top: 8, right: 24, left: 0, bottom: 24 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey="dateShort"
            angle={-45}
            textAnchor="end"
            height={56}
            tick={{ fontSize: 11 }}
          />
          <YAxis
            yAxisId="left"
            domain={['auto', 'auto']}
            tick={{ fontSize: 11 }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            domain={[0, 100]}
            tick={{ fontSize: 11 }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ paddingTop: '4px' }} iconType="line" />

          <Bar
            yAxisId="left"
            dataKey="fwd_20d"
            name={t('sentiment.behavior.similar_days.legend_fwd_return', 'Fwd 20d Return')}
            fill="#8884d8"
            radius={[4, 4, 0, 0]}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={(entry.fwd_20d ?? 0) >= 0 ? '#4caf50' : '#d32f2f'}
              />
            ))}
          </Bar>

          <Line
            yAxisId="right"
            type="monotone"
            dataKey="panic"
            name={t('sentiment.behavior.similar_days.legend_panic_score', 'Panic Score')}
            stroke="#ff7300"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ r: 3 }}
            isAnimationActive={false}
          />

          <Line
            yAxisId="right"
            type="monotone"
            dataKey="fomo"
            name={t('sentiment.behavior.similar_days.legend_fomo_score', 'FOMO Score')}
            stroke="#00c9ff"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={{ r: 3 }}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>

      <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.75, lineHeight: 1.45 }}>
        {stats && stats.count > 0 ? (() => {
          const params = {
            count: stats.count,
            winRate: stats.winRate.toFixed(0),
            median: `${stats.medianReturn >= 0 ? '+' : ''}${stats.medianReturn.toFixed(2)}`,
            days: 20,
          };

          if (stats.winRate >= 55 && stats.medianReturn > 0) {
            return t('sentiment.behavior.events.takeaway_positive', params);
          }
          if (stats.winRate < 45 || stats.medianReturn < 0) {
            return t('sentiment.behavior.events.takeaway_caution', params);
          }
          return t('sentiment.behavior.events.takeaway_neutral', params);
        })() : (
          t('sentiment.behavior.similar_days.interpretation_none', 'No historical precedent available for comparison.')
        )}
      </Typography>
    </Box>
  );
};

SimilarDaysVisualization.propTypes = {
  referenceDate: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

export default SimilarDaysVisualization;
